import { useEffect, useState } from "react"

import { TrocaDeSenha } from "./Perfil"
import { ErroDaApi, api, type Conta, type Perfil as PerfilDaConta } from "../api"
import { Badge } from "../componentes/Badge"
import { Button } from "../componentes/Button"
import * as Icones from "../componentes/Icones"
import { MolduraDeEntrada, SOBRETITULO_DA_ENTRADA } from "../componentes/MolduraDeEntrada"
import { MAIUSCULA } from "../lib/classes"

type Props = {
  conta: Conta
  aoTrocar: (credencial: string, perfil: PerfilDaConta) => void
  aoAdiar: () => void
}

export function PrimeiroAcesso({ conta, aoTrocar, aoAdiar }: Props) {
  const [perfil, definirPerfil] = useState<PerfilDaConta | null>(null)
  const [erro, definirErro] = useState<string | null>(null)

  useEffect(() => {
    api
      .perfil()
      .then(definirPerfil)
      .catch((falha: unknown) =>
        definirErro(
          falha instanceof ErroDaApi
            ? falha.regra
            : "Não foi possível alcançar o servidor.",
        ),
      )
  }, [])

  const carregando = perfil === null && erro === null

  const botaoAdiar = conta.troca_de_senha_bloqueia ? null : (
    <Button
      size="sm"
      variant="outline"
      type="button"
      onClick={() => {
        void api.adiarTrocaDeSenha().catch(() => undefined).finally(aoAdiar)
      }}
    >
      Trocar depois e entrar no sistema
      <Icones.Avancar />
    </Button>
  )

  return (
    <MolduraDeEntrada largo>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={SOBRETITULO_DA_ENTRADA}>Primeiro acesso</p>
            <Badge className="min-w-0">
              <Icones.Pessoa />
              <span className="max-w-60 truncate">{conta.nome}</span>
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-md border border-border bg-muted p-2 text-status-atencao">
              <Icones.Chave tamanho={20} />
            </span>
            <div className="flex flex-col gap-1">
              <h1 className="m-0 text-base font-semibold text-foreground">Troque a senha antes de começar</h1>
              <p className="m-0 text-xs text-muted-foreground">Segurança e autenticidade de auditoria</p>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-md border border-status-atencao bg-muted p-4 text-sm">
          <span className="mt-0.5 shrink-0 text-status-atencao">{/* anti-ia-ok: nome de token Tailwind, não prosa */}
            <Icones.Risco tamanho={18} />
          </span>
          <div className="flex flex-col gap-2">
            <p>
              Você entrou com uma <strong>senha provisória</strong> escolhida
              por quem criou ou redefiniu esta conta. Enquanto não for
              alterada, outras pessoas com acesso inicial conhecem esta
              credencial.
            </p>
            <p className="text-xs text-muted-foreground">
              Isso significa que atestes, ocorrências ou conferências
              realizados sob esta conta não garantem a identificação
              exclusiva de sua autoria legal.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-md border border-border bg-muted p-4 text-sm text-muted-foreground">
          <span className="mt-0.5 shrink-0">
            <Icones.Informacao tamanho={18} />
          </span>
          {conta.troca_de_senha_bloqueia ? (
            <p>
              <strong className="text-foreground">Exigência obrigatória:</strong>{" "}
              esta entidade exige a troca imediata no primeiro acesso
              (configurado pelo órgão, com data e fundamento legal).
            </p>
          ) : (
            <p>
              <strong className="text-foreground">Troca flexível:</strong>{" "}
              esta entidade sugere a alteração agora, mas permite que você
              adie para depois (no menu com o seu nome, no alto da tela, em{" "}
              <strong className="text-foreground">Segurança</strong>).
              O sistema registrará que o acesso ocorreu sob senha provisória.
            </p>
          )}
        </div>

        {erro !== null && (
          <div className="flex items-start gap-3 rounded-md border border-status-erro bg-muted p-4 text-sm">
            <span className="mt-0.5 shrink-0 text-status-erro">
              <Icones.Erro tamanho={18} />
            </span>
            <div className="flex flex-col">
              <span className="font-semibold">Não foi possível carregar o perfil</span>
              <span className={MAIUSCULA}>{erro}</span>
            </div>
          </div>
        )}

        {carregando && (
          <p className="py-6 text-center text-sm text-muted-foreground italic">
            Carregando o formulário de troca de senha...
          </p>
        )}

        {perfil !== null && <TrocaDeSenha perfil={perfil} aoTrocar={aoTrocar} semSaida forma="na-entrada" />}

        {!carregando && botaoAdiar !== null && <div className="flex justify-end">{botaoAdiar}</div>}
      </div>
    </MolduraDeEntrada>
  )
}
